import { Feature } from "geojson";

import { GeoJsonFeatureCollection } from "@/types/isochorne";
import { IsochroneData } from "@/types/spatial";

/**
 * Retrieves invalid contour values from an array of IsochroneData.
 *
 * A contour is considered invalid if the feature's geometry type is not "GeometryCollection"
 * and the coordinates array is empty.
 *
 * This function traverses all features in the isochrone data, filters out the invalid ones,
 * and returns a list of contour values that are of type number.
 *
 * @param data - An array of IsochroneData to be inspected.
 * @returns An array of invalid contour values as numbers.
 */

export function getInvalidContours(data: IsochroneData[]): number[] {
  return data
    .flatMap((item) => item.data || [])
    .flatMap((collection) => collection.features || [])
    .filter(
      (feature) =>
        feature.geometry.type !== "GeometryCollection" &&
        Array.isArray(feature.geometry.coordinates) &&
        feature.geometry.coordinates.length === 0
    )
    .map((feature) => feature.properties?.contour)
    .filter((contour): contour is number => typeof contour === "number");
}

/**
 * Re-expands a de-duped bulk isochrone response onto the coordinates that were
 * asked for, so `position` stays aligned with the caller's marker/row order
 * rather than with the distinct list.
 *
 * @param featureCollection - The single collection `POST /v1/isochrone` returns.
 * @param requestedCoordinates - The original, non-distinct `"lng,lat"` list.
 */
export const groupIsochroneByCoordinate = (
  featureCollection: GeoJsonFeatureCollection | undefined,
  requestedCoordinates: string[]
): IsochroneData[] => {
  const featuresByCoordinate = featureCollection?.features?.reduce<
    Record<string, Feature[]>
  >((accumulator, feature) => {
    // The input point this ring was cut from, echoed back by the BE. A shared
    // coordinate fans back out to every caller position through it.
    const [longitude, latitude] = feature.properties.coordinate;
    const coordinateKey = `${longitude},${latitude}`;

    accumulator[coordinateKey] = accumulator[coordinateKey] ?? [];
    accumulator[coordinateKey].push(feature);

    return accumulator;
  }, {});

  return requestedCoordinates.map((coordinate, index) => ({
    position: index + 1,
    data: [
      {
        type: "FeatureCollection" as const,
        features: featuresByCoordinate?.[coordinate] ?? []
      }
    ]
  }));
};
